import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, Search, RefreshCw, FlaskConical, Calendar, ArrowRight } from 'lucide-react';

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

export default function ZohoEntityList({
  entities, form, onSelect,
  dateFrom, dateTo, search, excludeTest,
  onDateFromChange, onDateToChange, onSearchChange, onExcludeTestChange,
  onRefresh,
}) {
  return (
    <div className="space-y-4" data-testid="entity-list-section">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={excludeTest ? 'default' : 'outline'}
          size="sm"
          onClick={() => onExcludeTestChange(!excludeTest)}
          data-testid="entity-excl-test"
          className="gap-1"
        >
          <FlaskConical className="h-3.5 w-3.5" />
          Excl. Test
        </Button>
        <div className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="date"
            value={dateFrom}
            onChange={e => onDateFromChange(e.target.value)}
            className="h-8 w-[140px] text-xs"
            placeholder="From"
            data-testid="entity-date-from"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={e => onDateToChange(e.target.value)}
            className="h-8 w-[140px] text-xs"
            placeholder="To"
            data-testid="entity-date-to"
          />
        </div>
        <div className="flex items-center gap-1">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={`Search by ${form.primary_key_field || 'ID'}...`}
            className="h-8 w-[180px] text-xs"
            data-testid="entity-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="entity-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
        </Button>
        <span className="text-sm text-muted-foreground ml-auto">{entities.length} record{entities.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Entity cards */}
      {entities.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-2 opacity-20" />
            <p className="font-medium">No records found</p>
            <p className="text-sm mt-1">Adjust filters or wait for submissions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {entities.map(ent => (
            <Card
              key={ent.primary_key}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onSelect(ent.primary_key)}
              data-testid={`entity-card-${ent.primary_key}`}
            >
              <CardContent className="pt-4 pb-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {ent.display_name || ent.primary_key}
                    </p>
                    {ent.display_name && (
                      <p className="text-xs text-muted-foreground font-mono">{form.primary_key_field}: {ent.primary_key}</p>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs shrink-0 ml-2">
                    {ent.submission_count} submission{ent.submission_count !== 1 ? 's' : ''}
                  </Badge>
                </div>
                {ent.periods.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {ent.periods.slice(0, 3).map(p => (
                      <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                    ))}
                    {ent.periods.length > 3 && (
                      <Badge variant="outline" className="text-xs">+{ent.periods.length - 3}</Badge>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 border-t text-xs text-muted-foreground">
                  <span>Last: {formatDate(ent.latest_date)}</span>
                  <span className="text-primary flex items-center gap-1 font-medium">
                    View <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
